import { Box, Stack, Typography } from "@mui/material";
import { Transaction } from "../../../../models";
import { formatCurrency, formatDate } from "../../../../utils/formatters";
import { SideDrawer } from "../../../shared/SideDrawer";
import { Plan } from "../../../../models/accounts/plan";
import { Tag } from "../../../../models/tag";

interface AggregationTransactionDrawerProps {
  transaction: Transaction | null;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  employerId?: string;
  employees?: { id: string; firstName: string; lastName: string; customerEmployeeId?: string; tags?: Tag[] }[];
  accounts?: { id: string; name: string }[];
  serviceSectors?: { id: string; name: string }[];
  plans?: Plan[];
}

const formatAmount = (amount: number) => {
  // If amount is negative, show it as positive with a plus sign
  // If amount is positive, show it as is
  if (amount < 0) {
    return `+${formatCurrency(-amount)}`;
  }
  return formatCurrency(amount);
};

export const AggregationTransactionDrawer: React.FC<AggregationTransactionDrawerProps> = ({
  transaction,
  isOpen,
  setIsOpen,
  employerId,
  employees,
  accounts,
  serviceSectors,
  plans
}) => {
  const employee = employees?.find(e => e.id === transaction?.employeeId);

  return (
    <SideDrawer
      title="Aggregation Transaction Details"
      open={isOpen}
      setIsOpen={setIsOpen}
    >
      <Stack spacing={3} mx={2}>
        <Box>
          <Typography variant="subtitle2" color="black" fontWeight="bold">
            Amount
          </Typography>
          <Typography variant="body1">
            {transaction?.billingAmount ? formatAmount(transaction.billingAmount.amount) : "-"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="black" fontWeight="bold">
            Date
          </Typography>
          <Typography variant="body1">
            {transaction?.createdAt ? formatDate(transaction.createdAt) : "-"}
          </Typography>
        </Box>

        {transaction?.charges && transaction.charges.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="black" fontWeight="bold">
              Account
            </Typography>
            <Stack spacing={1}>
              {transaction.charges.map((charge, index) => {
                const plan = plans?.find(p => p.id === charge.planId);
                return (
                  <Typography key={index} variant="body1">
                    {plan?.name || charge.planId} {formatAmount(charge.amount.amount)}
                  </Typography>
                );
              })}
            </Stack>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" color="black" fontWeight="bold">
            User
          </Typography>
          <Typography variant="body1">
            {employee ? `${employee.firstName} ${employee.lastName}` : "-"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="black" fontWeight="bold">
            Employee Id
          </Typography>
          <Typography variant="body1">
            {employee?.customerEmployeeId || "-"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="black" fontWeight="bold">
            Category
          </Typography>
          <Typography variant="body1">
            {serviceSectors?.find(
              (s) => s.id === transaction?.serviceSectorId
            )?.name || "-"}
          </Typography>
        </Box>

        {employee?.tags && employee.tags.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="black" fontWeight="bold">
              Tags
            </Typography>
            <Stack spacing={1}>
              {employee.tags.map((tag, index) => (
                <Typography key={index} variant="body1">
                  {tag.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </SideDrawer>
  );
}; 
